<template>
  <div class="multiselect-dropdown">
    <div class="dropdown">
      <b-button
        variant="none"
        size="md"
        :disabled="isDisabled"
        class="bg-white shadow-sm rounded cursor-pointer d-flex align-items-center gap-5"
        @click="toggleDropdown"
      >
        <div class="text-sm text-thick">
          {{ itemName }} <span class="text-primary">({{ selectedOptions.length }} of {{ filteredItems.length }})</span>
        </div>
        <GjIcon
          :class="isDropdownOpen ? 'rotate-180' : ''"
          size="18"
          name="ArrowDown"
        />
      </b-button>
      <ul
        v-show="isDropdownOpen"
        class="rounded shadow-sm scrolling-area"
        @scroll="handleDropdownScroll"
      >
        <input
          v-if="items.length > 5"
          :id="`${itemName}-search`"
          v-model.trim="filter"
          type="search"
          :placeholder="`Search ${itemName}`"
          class="position-sticky shadow-sm"
          :name="`${itemName}-search`"
        >
        <div
          v-if="items.length > 5"
          class="d-flex justify-center px-1"
        >
          <input
            id="select-all"
            v-model="allSelected"
            type="checkbox"
            :value="allSelected"
            :checked="allSelected"
          >
          <h6 class="m-0 ml-1">
            Select All
          </h6>
        </div>
        <hr v-if="items.length > 5">
        <li
          v-for="(option, index) in filteredItems"
          :key="index"
          class="d-flex align-items-center gap-10"
          @click="toggleOption(checkFilterType(option))"
        >
          <input
            :id="checkFilterType(option)"
            type="checkbox"
            :value="checkFilterType(option)"
            :checked="selectedOptions.includes(checkFilterType(option))"
          >
          {{ checkValueType(option) }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    isDisabled: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => ([]),
    },
    itemName: {
      type: String,
      default: '',
    },
    isForDistribution: {
      type: Boolean,
      default: false,
    },
    selectedAccount: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      selectedOptions: [],
      isDropdownOpen: false,
      currentPage: 0,
      initialSelectionDone: false,
      filter: '',
      typingTimer: null,
      doneTypingInterval: 500,
      filteredItems: this.items,
      allSelected: false,
    }
  },
  watch: {
    selectedAccount() {
      if (!this.isForDistribution) {
        this.selectedOptions = [this.items[0].name]
      }
    },
    selectedOptions() {
      this.$refs.selectOptionsRef = this.selectedOptions
    },
    allSelected() {
      if (this.allSelected) {
        const allItems = this.isForDistribution ? this.filteredItems.map(x => x.distributionId) : this.filteredItems.map(x => x.name)
        this.selectedOptions = allItems
      } else {
        this.selectedOptions = this.isForDistribution ? [this.items[0].distributionId] : [this.items[0].name]
      }
      this.optionsChanged()
    },
    filter() {
      clearTimeout(this.typingTimer)
      this.typingTimer = setTimeout(() => {
        this.onChangeSearch()
      }, this.doneTypingInterval)
    },
  },
  mounted() {
    this.handleDropdownScroll()
    document.addEventListener('click', this.closeDropdownOnClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeDropdownOnClickOutside)
  },
  created() {
    this.fetchItems(true)
    if (!this.isForDistribution) {
      this.selectedOptions = [this.items[0].name]
    }
  },
  methods: {
    toggleOption(optionId) {
      if (this.selectedOptions.includes(optionId)) {
        this.selectedOptions = this.selectedOptions.filter(
          id => id !== optionId,
        )
        this.$emit('idsChanged', this.selectedOptions)
      } else {
        this.selectedOptions.push(optionId)
        this.$emit('idsChanged', this.selectedOptions)
      }
    },
    optionsChanged() {
      this.$emit('idsChanged', this.selectedOptions)
    },
    onChangeSearch() {
      if (this.isNullOrEmpty(this.filter)) {
        this.filteredItems = this.items
      }
      if (this.isForDistribution) {
        this.filteredItems = this.items.filter(x => x.domainName.toLowerCase().includes(this.filter.toLowerCase()))
      } else {
        this.filteredItems = this.items.filter(x => x.name.toLowerCase().includes(this.filter.toLowerCase()))
      }
    },
    fetchItems(isInitalRequest) {
      const query = {
        currentPage: this.currentPage,
        initialSelectionDone: this.initialSelectionDone,
        selectedOptions: this.selectedOptions,
      }

      this.$emit('fetchData', query)

      if (isInitalRequest) {
        this.initialSelectionDone = true
      }
    },
    removeOption(index) {
      this.selectedOptions.splice(index, 1)
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen
    },
    handleDropdownScroll() {
      const scrollingList = document.querySelector('.scrolling-area')
      scrollingList.addEventListener('scroll', this.handleScroll)
    },
    closeDropdownOnClickOutside(event) {
      const dropdown = this.$el.querySelector('.dropdown')
      if (dropdown && !dropdown.contains(event.target)) {
        this.isDropdownOpen = false
      }
    },
    handleScroll(event) {
      const scrollingList = event.target
      const scrollableHeight = scrollingList.scrollHeight - scrollingList.clientHeight
      const scrolledPercentage = (scrollingList.scrollTop / scrollableHeight) * 100

      if (scrolledPercentage >= 80) {
        this.currentPage++

        this.fetchItems()
      }
    },
    checkFilterType(option) {
      return this.isForDistribution ? option.distributionId : option.name
    },
    checkValueType(option) {
      return this.isForDistribution ? option.domainName : option.name
    },
  },
}
</script>

<style scoped>
.multiselect-dropdown {
  display: inline-block;
  position: relative;
  min-width: 160px;
  max-width: auto;
}

.custom-padding{
  padding: 12px;
  min-width: 160px;
  max-width: auto;
}

.gap-10{
  gap:10px;
}
.gap-5{
  gap:5px;
}

.selected-items {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #f9f9f9;
}

.selected-item {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #ddd;
  margin-right: 4px;
}

.selected-item span {
  margin-left: 4px;
  cursor: pointer;
}

.dropdown {
  position: relative;
}

input[type='search'] {
  width: 100%;
  border: none;
  border-radius: 5px;
  position: sticky;
  top: 0;
  box-shadow: 0 0 16px 10px rgba(0, 0, 0, 0);
  outline-style: none;
  padding: 10px;
  margin-bottom: 10px;
}

ul {
  list-style: none;
  padding: 0;
  margin: 0;
  position: absolute;
  max-height: 180px;
  z-index: 10;
  overflow-y: auto;
  top: 50px;
  right: 0px;
  width: 230px;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.1);
  border-top: none;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  background-color: #fff;
}

li {
  padding: 8px;
  cursor: pointer;
}

li:hover {
  background-color: #f1f6fe;
}
</style>
